<template>
  <div class="nvr-live app-container">
    <div class="header">
      <div class="item">
        设备名称: {{name || '--'}}
      </div>
      <div class="item">
        通道ID: {{channelId}}
      </div>
    </div>
    <div class="player-container">
      <!-- v-if 而不是 :src="playSrc" 初值空串：<video src=""> 会把当前页面 URL 当媒体源
           先加载一次并进错误态，Safari 对这个初始错误态比 Chrome 敏感。 -->
      <video
        v-if="playSrc"
        class="video"
        autoplay
        playsinline
        :muted="needMuted"
        :src="playSrc"
        controls
        @canplay="onCanplay"
      ></video>
    </div>
  </div>
</template>

<script>
import { getNvrPlayUrl } from '@/api/nvr'

export default {
  data() {
    return {
      name:  this.$route.query.channelName,
      channelId: this.$route.query.channelId,
      cameraId: this.$route.query.cameraId,
      nvrId: this.$route.query.nvrId,
      src: ''
    }
  },
  computed: {
    // 判据和 views/live/index.vue 保持一致：iPhone/iPad 的 WebKit 没有 window.MediaSource
    // （iOS 17.1+ 给的是 ManagedMediaSource）。桌面和安卓都有，所以下面这些 iOS 专属分支
    // 在 PC 上恒不生效。
    isIOS() {
      return !('MediaSource' in window)
    },
    // iOS 不允许带音轨的视频自动播放，而这个 <video> 是 autoplay + 在 canplay 里主动
    // play()，两条路都会被拒，页面上看就是"打不开"。
    needMuted() {
      return this.isIOS
    },
    // 后端的 .live.mp4 是 ZLMediaKit 的 HTTP-FMP4：moov 里 mvhd 的 duration 是 0，body 是
    // 永不结束的 styp/sidx/moof/mdat，而且不支持 Range（带 Range 请求回 200 不回 206）。
    // Chrome 认这种"时长会增长的流"，Safari 的渐进式 MP4 路径不认，加载阶段就失败且不抛错，
    // 表现就是"点了没反应也不报错"。同一个 stream id 的 HLS 入口 iOS 原生就能播（不需要 MSE，
    // 所以 iOS 17.1 以下也行），因此只在 iOS 上换地址，PC 端维持原样。
    playSrc() {
      if (!this.isIOS) return this.src
      // 只换路径，可能存在的 query 原样保留
      return this.src.replace(/\.live\.mp4(\?.*)?$/, '/hls.m3u8$1')
    }
  },
  mounted() {
    this.getPlayUrl()
  },
  methods: {
    async getPlayUrl() {
      const res = await getNvrPlayUrl({
        nvrId: this.nvrId,
        cameraId: this.cameraId
      })
      this.src = location.protocol === 'http:' ? res.data.fmp4 : res.data.https_fmp4
    },
    onCanplay(e) {
      // iOS 上 play() 返回 Promise，非用户手势触发时会被拒。不接住就是一条静默的
      // unhandled rejection —— 表现恰好是"点了没反应，也不报错"。
      const p = e.target.play()
      if (p && typeof p.catch === 'function') p.catch(() => {})
    }
  }
}
</script>

<style>
.nvr-live {
  .header {
    display: flex;
    padding-bottom: 20px;
    border-bottom: 1px solid #ccc;
    .item {
      margin-right: 40px;
    }
  }
  .player-container {
    position: relative;
    .video {
      object-fit: contain;
      position: absolute;
      top: 20px;
      left: 0;
      width: calc(100vw - 300px);
      height: calc(100vh - 240px);
    }
  }
}

/*
  上面那条 .video 是绝对定位 + width: calc(100vw - 300px)，那 300px 是留给固定侧边栏的。
  移动端侧边栏已经改成抽屉、不再占宽，375px 上算出来只剩 75px（正好屏宽的 1/5），画面缩成
  一条竖缝；而且 .player-container 的子元素全是绝对定位，高度塌成 0，560px 那个高度是
  100vh-240px 硬凑的，和宽度不成比例，object-fit: contain 又把画面缩到框中间。
  窄屏改回常规流：跟随内容宽度、按原始比例撑高。桌面端不受影响。

  这条媒体查询写在 .nvr-live 外面、选择器自己带前缀，是有意为之：这个 <style> 没有
  lang="less"，上面那些嵌套是浏览器按原生 CSS 嵌套解析的，而「嵌套的 @media」要 Safari 17.2
  才认（嵌套普通规则 16.5 就行），写在里面会在低版本 iOS 上整段失效。平铺写法所有版本都认。
  特异性两者相同（原生嵌套的 :is(.nvr-live) 和一个类选择器等价），且这条在后面，能盖住。
*/
@media (max-width: 768px) {
  .nvr-live .player-container .video {
    position: static;
    width: 100%;
    height: auto;
  }
}
</style>
